import UploadFileIcon from '@mui/icons-material/UploadFile'
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material'
import { useState } from 'react'

import { securityInsightsApi } from 'hooks/api/securityInsights'
import { Option } from 'types/option'

import PollUserSelect from './pollUserSelect'

type Row = {
  securityId: string
  targetURL: string
  title: string
}

const options: Option[] = [
  { value: 1, label: '簡易的にもっと働きたいと聞く' },
  { value: 2, label: '望ましくない勤務条件を聞く' },
]

// CSVを解析して Row[] を返却
async function readCsvRows(file: File): Promise<Row[]> {
  const text = await file.text()
  const lines = text.trim().split(/\r?\n/)
  if (lines.length < 2) return []

  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase())
  const sidIdx = headers.indexOf('securityid')
  const urlIdx = headers.indexOf('targeturl')
  const titleIdx = headers.indexOf('title')
  if (sidIdx === -1 || urlIdx === -1 || titleIdx === -1) {
    throw new Error('CSV に "securityId", "targetURL", "title" 列が必要です')
  }

  const rows: Row[] = []
  for (let i = 1; i < lines.length; i++) {
    const raw = lines[i]
    if (!raw.trim()) continue
    const cols = raw.split(',')
    const securityId = (cols[sidIdx] ?? '').trim()
    const targetURL = (cols[urlIdx] ?? '').trim()
    const title = (cols[titleIdx] ?? '').trim()
    if (securityId && targetURL && title) {
      rows.push({ securityId, targetURL, title })
    }
  }
  return rows
}

export default function HypothesisTab() {
  const [rows, setRows] = useState<Row[]>([])
  const [type, setType] = useState(0)

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const parsed = await readCsvRows(file)
      setRows(parsed)
    } catch (err) {
      alert((err as Error).message)
    }
  }

  const handleSubmit = async () => {
    if (!confirm('送信しますか？')) return
    if (!rows.length || !type) {
      alert('必須項目が未入力です')
      return
    }

    const value = options.find((o) => o.value === type)?.value

    // タイプに応じてデフォルトのタイトルとURLを設定
    const processedRows = rows.map((row) => {
      let defaultTitle = row.title
      let defaultUrl = row.targetURL

      if (!row.title || !row.targetURL) {
        if (type === 1) {
          // もっと働きたいかどうか
          defaultTitle = row.title || 'お仕事のペースについてのお聞かせください'
          defaultUrl = row.targetURL || '/insight/more-work-simple'
        } else if (type === 2) {
          // 望ましくない勤務条件
          defaultTitle = row.title || '勤務環境改善のためのアンケート'
          defaultUrl = row.targetURL || '/insight/undesired-work-condition'
        }
      }

      return {
        ...row,
        title: defaultTitle,
        targetURL: defaultUrl,
      }
    })

    await securityInsightsApi().bulkCreate({
      rows: processedRows,
      type: Number(value),
    })

    alert(`送信しました (行数: ${rows.length} 件)`)
    setRows([])
    setType(0)
  }

  return (
    <Box p={2}>
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center' }}
      >
        <Button
          component="label"
          variant="contained"
          startIcon={<UploadFileIcon />}
          sx={{ mr: 2 }}
        >
          CSV アップロード
          <input type="file" accept=".csv" hidden onChange={handleFile} />
        </Button>
        <Typography variant="body2">
          {rows.length
            ? `読み込み済み行数: ${rows.length}`
            : '※ securityId,targetURL,title の列を持つ CSV を選択してください（targetURL・titleが空の場合は自動設定されます）'}
        </Typography>
      </Paper>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="action-label">アクション</InputLabel>
        <Select
          labelId="action-label"
          value={type}
          label="アクション"
          onChange={(e) => setType(Number(e.target.value))}
        >
          <MenuItem value="">未選択</MenuItem>
          {options.map((st) => (
            <MenuItem key={st.value} value={st.value}>
              {st.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* CSVプレビュー */}
      {!!rows.length && (
        <Paper
          variant="outlined"
          sx={{ mb: 3, maxHeight: 360, overflow: 'auto' }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>securityId</TableCell>
                <TableCell>title</TableCell>
                <TableCell>targetURL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={`${r.securityId}-${i}`}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{r.securityId}</TableCell>
                  <TableCell>{r.title}</TableCell>
                  <TableCell>{r.targetURL}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Button
        variant="contained"
        disabled={!rows.length || !type}
        onClick={handleSubmit}
      >
        送信
      </Button>
      <Box>
        <PollUserSelect />
      </Box>
    </Box>
  )
}
